import { tokenRequest } from '../authConfig';
import { InteractionRequiredAuthError, IPublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import { AccountInfo, SilentRequest } from '@azure/msal-browser';

// Redirection MSAL en cours (connexion ou renouvellement), `null` sinon.
//
// Elle ne doit être tenue que le temps où la page est réellement en train de
// partir : MSAL fait `location.assign(...)` puis ne règle sa promesse qu'après
// un délai de sécurité (30 s). Si nous reprenons la main après ce règlement,
// c'est que la navigation n'a pas eu lieu — réseau coupé (mode avion),
// interaction déjà en cours, onglet externe refermé sur une page d'erreur — et
// elle doit être relâchée. L'ancien drapeau booléen, jamais rabaissé,
// condamnait tout appel ultérieur à une promesse jamais réglée pour toute la
// vie de la page : plus aucune requête API ne partait, sans la moindre erreur,
// jusqu'à ce que le processus soit tué (constat du 09/09/2026 : progression de
// lecture perdue après un épisode en mode avion sur le lecteur mobile).
let redirectInFlight: Promise<void> | null = null;

/**
 * Lance une redirection MSAL et ne rend la main qu'en cas d'échec de la
 * navigation : dans le cas nominal la page est déchargée avant que la promesse
 * de MSAL ne se règle. Rejette toujours si l'on est encore là, pour que
 * l'appelant échoue franchement au lieu de rester suspendu.
 */
async function redirectOrFail(redirect: () => Promise<void>, what: string): Promise<never> {
  // Deux appels partis en même temps arrivent tous deux ici (l'acquisition
  // silencieuse est asynchrone) : le second se joint à la redirection du
  // premier au lieu d'en lancer une autre.
  if (!redirectInFlight) {
    const attempt = redirect();
    const mine = attempt.then(() => undefined, () => undefined);
    redirectInFlight = mine;
    try {
      await attempt;
    } catch (redirectError: any) {
      if (redirectError?.errorCode === 'interaction_in_progress') {
        console.warn(`⚠️ [getTokenRedirect] Interaction déjà en cours, ${what} abandonnée pour cet appel`);
      }
      throw redirectError;
    } finally {
      if (redirectInFlight === mine) redirectInFlight = null;
    }
    throw new Error(`[getTokenRedirect] ${what} impossible : la page n'a pas été redirigée (réseau indisponible ?)`);
  }
  await redirectInFlight;
  throw new Error(`[getTokenRedirect] ${what} impossible : la redirection en cours n'a pas abouti`);
}

/**
 * Récupère un accessToken MSAL de façon silencieuse ou via redirection si nécessaire
 * @param request Objet de requête MSAL (ex: tokenRequest)
 * @param msalInstance Instance MSAL (useMsal().instance)
 */
export async function getTokenRedirect(request: typeof tokenRequest, msalInstance: IPublicClientApplication): Promise<string> {
  // Redirection en cours : attendre qu'elle emporte la page. Si elle rend la
  // main, c'est qu'elle a échoué — échouer aussi plutôt que de relancer une
  // seconde redirection dans les mêmes conditions.
  if (redirectInFlight) {
    console.log('⏳ [getTokenRedirect] Redirection déjà en cours, attente...');
    await redirectInFlight;
    throw new Error('[getTokenRedirect] La redirection en cours n\'a pas abouti');
  }

  const account = msalInstance.getActiveAccount();
  
  if (!account) {
    console.error('❌ [getTokenRedirect] Aucun compte MSAL actif');
    // Vérifier s'il existe des comptes dans le cache
    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      console.log('🔧 [getTokenRedirect] Compte trouvé dans le cache, activation...');
      msalInstance.setActiveAccount(accounts[0]);
      // Réessayer avec le compte activé
      return getTokenRedirect(request, msalInstance);
    }
    
    // Aucun compte disponible, redirection vers login complet
    console.log('🔄 [getTokenRedirect] Redirection vers la page de connexion complète...');
    return redirectOrFail(() => msalInstance.loginRedirect(request), 'connexion');
  }
  
  const silentRequest: SilentRequest = { ...request, account: account as AccountInfo };
  
  try {
    console.log('🔄 [getTokenRedirect] Tentative d\'acquisition silencieuse du token...');
    console.log('🔍 [getTokenRedirect] Scopes demandés:', request.scopes);
    const response: AuthenticationResult = await msalInstance.acquireTokenSilent(silentRequest);
    
    console.log('📦 [getTokenRedirect] Réponse MSAL:', {
      hasAccessToken: !!response.accessToken,
      hasIdToken: !!response.idToken,
      scopes: response.scopes,
      tokenType: response.tokenType
    });
    
    if (!response.accessToken) {
      console.error('❌ [getTokenRedirect] Aucun accessToken dans la réponse');
      console.error('💡 Astuce: Vérifiez que les scopes incluent bien votre API scope');
      console.error('📋 Scopes dans la réponse:', response.scopes);
      throw new InteractionRequiredAuthError();
    }
    console.log('✅ [getTokenRedirect] Token acquis silencieusement');
    return response.accessToken;
  } catch (error) {
    // Si l'erreur est InteractionRequiredAuthError, le token a expiré
    if (error instanceof InteractionRequiredAuthError) {
      console.warn('⚠️ [getTokenRedirect] Token expiré ou scope manquant, redirection pour renouvellement...');
      // Dans le cas nominal la page est déchargée et cet appel ne rend jamais
      // la main ; sinon il rejette, et le verrou est relâché.
      return redirectOrFail(() => msalInstance.acquireTokenRedirect(silentRequest), 'redirection de renouvellement');
    }
    console.error('❌ [getTokenRedirect] Erreur inattendue:', error);
    throw error;
  }
}
